/**
 * College career hub: one school, public seasons (2010+), from the class summary.
 * Ranked by category fantasy points — not yards / TDs / tackles.
 */

import { CURRENT_YEAR, FIRST_YEAR } from '../constants/app';
import type { ClassSummaryFile, ClassSummaryRow } from '../types/classSummary';
import { PLAYER, ROW } from '../types/classSummary';
import type { DraftRoundWorkerParams } from './draftRoundFilter';
import {
  ClassStatCategory,
  classRowPoints,
  classRowStatPoints,
  parseClassStatParam,
  rowMatchesClassFilters,
} from './classCompare';
import {
  CollegeMeta,
  collegeIdForDirectorySlug,
  collegeIdForSlug,
  resolveCollegeMeta,
} from './collegeMeta';

export const CAREER_SEGMENT = 'career';
export const CAREER_YEARS_PARAM = 'years';

export const CAREER_YEAR_PRESETS = [3, 5] as const;

export type CareerYearFilter =
  | { kind: 'all' }
  | { kind: 'last'; count: number }
  | { kind: 'range'; from: number; to: number };

export interface CareerYearRange {
  from: number;
  to: number;
}

export interface CollegeCareerPath {
  slug: string;
  stat: ClassStatCategory;
}

export interface CareerFilters {
  positions: string[] | null;
  draft: DraftRoundWorkerParams | null;
  draftClass: { from: number; to: number } | null;
  stat: ClassStatCategory;
}

export const CAREER_CATEGORY_COLUMNS = [
  { id: 'pass', label: 'Pass', rowIndex: ROW.pass },
  { id: 'rush', label: 'Rush', rowIndex: ROW.rush },
  { id: 'rec', label: 'Rec', rowIndex: ROW.rec },
  { id: 'def', label: 'Def', rowIndex: ROW.def },
  { id: 'kick', label: 'Kick', rowIndex: ROW.kick },
  { id: 'punt', label: 'Punt', rowIndex: ROW.punt },
  { id: 'ret', label: 'Ret', rowIndex: ROW.ret },
] as const;

export interface CareerPlayerRow {
  playerIndex: number;
  name: string;
  pos: string | null;
  round: number | null;
  pick: number | null;
  classYear: number | null;
  seasons: number;
  firstSeason: number;
  lastSeason: number;
  categoryPoints: number;
  totalPoints: number;
  categories: number[];
  usedYtd: boolean;
  partial: boolean;
  collegeId?: string;
  collegeSlug?: string | null;
  collegeName?: string;
  collegeLogo?: string | null;
}

export interface CollegeCareerTable {
  college: CollegeMeta;
  stat: ClassStatCategory;
  fromYear: number;
  toYear: number;
  rows: CareerPlayerRow[];
  totalPoints: number;
  playerCount: number;
  seasonCount: number;
  ytdWeek: number;
  currentSeasonIncomplete: boolean;
  currentSeason: number;
}

const isSeasonYear = (value: number): boolean =>
  Number.isInteger(value) && value >= FIRST_YEAR && value <= CURRENT_YEAR + 1;

const clampRange = (from: number, to: number): CareerYearRange => ({
  from: Math.max(FIRST_YEAR, Math.min(from, to)),
  to: Math.min(CURRENT_YEAR, Math.max(from, to)),
});

export const parseCollegeCareerPath = (pathname: string): CollegeCareerPath | null => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'college' || !parts[1] || parts[2] !== CAREER_SEGMENT) return null;
  if (parts.length > 4) return null;
  return {
    slug: parts[1],
    stat: parseClassStatParam(parts[3]),
  };
};

export const getCollegeCareerPath = (slug: string, stat: string = ''): string => {
  const category = parseClassStatParam(stat);
  return category
    ? `/college/${slug}/${CAREER_SEGMENT}/${category}`
    : `/college/${slug}/${CAREER_SEGMENT}`;
};

export const parseCareerYearsParam = (
  value: string | null | undefined
): CareerYearFilter => {
  if (!value) return { kind: 'all' };
  const trimmed = value.trim().toLowerCase();
  const last = trimmed.match(/^last(\d+)$/);
  if (last) {
    const count = parseInt(last[1], 10);
    if (CAREER_YEAR_PRESETS.includes(count as (typeof CAREER_YEAR_PRESETS)[number])) {
      return { kind: 'last', count };
    }
    return { kind: 'all' };
  }
  const range = trimmed.match(/^(\d{4})(?:-(\d{4}))?$/);
  if (range) {
    const a = parseInt(range[1], 10);
    const b = range[2] ? parseInt(range[2], 10) : a;
    if (!isSeasonYear(a) || !isSeasonYear(b)) return { kind: 'all' };
    return { kind: 'range', from: Math.min(a, b), to: Math.max(a, b) };
  }
  return { kind: 'all' };
};

export const serializeCareerYearsFilter = (filter: CareerYearFilter): string | null => {
  if (filter.kind === 'all') return null;
  if (filter.kind === 'last') return `last${filter.count}`;
  return filter.from === filter.to ? `${filter.from}` : `${filter.from}-${filter.to}`;
};

export const resolveCareerYearRange = (
  filter: CareerYearFilter,
  currentYear: number = CURRENT_YEAR
): CareerYearRange => {
  if (filter.kind === 'last') {
    return clampRange(currentYear - filter.count + 1, currentYear);
  }
  if (filter.kind === 'range') {
    return clampRange(filter.from, filter.to);
  }
  return { from: FIRST_YEAR, to: currentYear };
};

const formatYearRange = ({ from, to }: CareerYearRange): string =>
  from === to ? `${from}` : `${from}–${to}`;

export const describeCareerYearFilter = (
  filter: CareerYearFilter,
  currentYear: number = CURRENT_YEAR
): string => {
  const range = resolveCareerYearRange(filter, currentYear);
  if (filter.kind === 'last') {
    return `Last ${filter.count} seasons (${formatYearRange(range)})`;
  }
  if (filter.kind === 'range') {
    return formatYearRange(range);
  }
  return `${FIRST_YEAR}–${currentYear}`;
};

export const resolveCareerCollegeId = (
  slug: string,
  summary: ClassSummaryFile | null
): string | null => {
  const fromDirectory = collegeIdForDirectorySlug(slug);
  if (fromDirectory) return fromDirectory;
  if (!summary) return null;
  const ids = new Set<string>();
  for (const row of summary.rows) ids.add(String(row[ROW.collegeId]));
  return collegeIdForSlug(slug, ids);
};

export const playerMatchesDraftClass = (
  classYear: number | null,
  draftClass: CareerFilters['draftClass']
): boolean => {
  if (!draftClass) return true;
  if (classYear == null) return false;
  return classYear >= draftClass.from && classYear <= draftClass.to;
};

export const matchesCareerFilters = (
  row: ClassSummaryRow,
  classYear: number | null,
  playerRound: number | null,
  yearRange: CareerYearRange,
  filters: CareerFilters
): boolean => {
  const season = row[ROW.season];
  if (season < yearRange.from || season > yearRange.to) return false;
  if (!playerMatchesDraftClass(classYear, filters.draftClass)) return false;
  return rowMatchesClassFilters(row, playerRound, {
    positions: filters.positions,
    draft: filters.draft,
    stat: filters.stat,
  });
};

const seasonSetSize = (rows: ClassSummaryRow[]): number => {
  const seasons = new Set<number>();
  rows.forEach((row) => seasons.add(row[ROW.season]));
  return seasons.size;
};

export const buildCollegeCareerTable = (
  summary: ClassSummaryFile,
  collegeId: string,
  yearRange: CareerYearRange,
  filters: CareerFilters
): CollegeCareerTable => {
  const matchedRows: ClassSummaryRow[] = [];
  const byPlayer = new Map<
    number,
    {
      name: string;
      pos: string | null;
      posSeason: number;
      round: number | null;
      pick: number | null;
      classYear: number | null;
      seasons: Set<number>;
      categoryPoints: number;
      totalPoints: number;
      categories: number[];
      usedYtd: boolean;
    }
  >();

  for (const row of summary.rows) {
    if (String(row[ROW.collegeId]) !== collegeId) continue;
    const player = summary.players[row[ROW.playerIndex]];
    if (!player) continue;
    const classYear = player[PLAYER.classYear];
    if (!matchesCareerFilters(row, classYear, player[PLAYER.round], yearRange, filters)) continue;
    const season = row[ROW.season];

    matchedRows.push(row);
    const usedYtd = Boolean(
      summary.currentSeasonIncomplete && season === summary.toYear
    );
    const categoryPoints = classRowStatPoints(row, summary, filters.stat);
    const totalPoints = classRowPoints(row, summary);
    const existing = byPlayer.get(row[ROW.playerIndex]);
    if (!existing) {
      byPlayer.set(row[ROW.playerIndex], {
        name: player[PLAYER.name],
        pos: row[ROW.pos],
        posSeason: season,
        round: player[PLAYER.round],
        pick: player[PLAYER.pick],
        classYear,
        seasons: new Set([season]),
        categoryPoints,
        totalPoints,
        categories: CAREER_CATEGORY_COLUMNS.map((column) => row[column.rowIndex] || 0),
        usedYtd,
      });
      continue;
    }

    existing.seasons.add(season);
    existing.categoryPoints += categoryPoints;
    existing.totalPoints += totalPoints;
    existing.usedYtd = existing.usedYtd || usedYtd;
    CAREER_CATEGORY_COLUMNS.forEach((column, index) => {
      existing.categories[index] += row[column.rowIndex] || 0;
    });
    if (season >= existing.posSeason) {
      existing.pos = row[ROW.pos];
      existing.posSeason = season;
    }
  }

  const rows: CareerPlayerRow[] = Array.from(byPlayer.entries())
    .map(([playerIndex, item]) => {
      const seasons = Array.from(item.seasons).sort((a, b) => a - b);
      return {
        playerIndex,
        name: item.name,
        pos: item.pos,
        round: item.round,
        pick: item.pick,
        classYear: item.classYear,
        seasons: seasons.length,
        firstSeason: seasons[0],
        lastSeason: seasons[seasons.length - 1],
        categoryPoints: parseFloat(item.categoryPoints.toFixed(2)),
        totalPoints: parseFloat(item.totalPoints.toFixed(2)),
        categories: item.categories.map((value) => parseFloat(value.toFixed(2))),
        usedYtd: item.usedYtd,
        partial: item.classYear != null && item.classYear < FIRST_YEAR,
      };
    })
    .sort(
      (a, b) =>
        b.categoryPoints - a.categoryPoints ||
        b.totalPoints - a.totalPoints ||
        a.name.localeCompare(b.name)
    );

  const totalPoints = parseFloat(
    rows.reduce((sum, row) => sum + row.categoryPoints, 0).toFixed(2)
  );

  return {
    college: resolveCollegeMeta(collegeId),
    stat: filters.stat,
    fromYear: yearRange.from,
    toYear: yearRange.to,
    rows,
    totalPoints,
    playerCount: rows.length,
    seasonCount: seasonSetSize(matchedRows),
    ytdWeek: summary.ytdWeek,
    currentSeasonIncomplete: summary.currentSeasonIncomplete,
    currentSeason: summary.toYear,
  };
};

export const careerUsesCurrentSeason = (table: CollegeCareerTable): boolean =>
  table.currentSeasonIncomplete &&
  table.fromYear <= table.currentSeason &&
  table.toYear >= table.currentSeason;

export const careerHasPartialPlayers = (table: CollegeCareerTable): boolean =>
  table.rows.some((row) => row.partial);
